import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { toast } from "react-toastify";
import useCompanySettings from "../../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.9rem"
  },
  infoBox: {
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  formField: {
    marginBottom: theme.spacing(2)
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    flexWrap: "wrap"
  }
}));

const TelephonyConfig = ({ settings }) => {
  const classes = useStyles();
  const { update } = useCompanySettings();
  const [enabled, setEnabled] = useState("disabled");
  const [provider, setProvider] = useState("zadarma");
  const [allowIncoming, setAllowIncoming] = useState("disabled");
  const [allowOutgoing, setAllowOutgoing] = useState("enabled");
  const [showOnTicket, setShowOnTicket] = useState("enabled");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [sipServer, setSipServer] = useState("pbx.zadarma.com");
  const [websocketUrl, setWebsocketUrl] = useState("wss://pbx.zadarma.com/ws");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.telephonyEnabled || "disabled");
    setProvider(settings.telephonyProvider || "zadarma");
    setAllowIncoming(settings.telephonyAllowIncoming || "disabled");
    setAllowOutgoing(settings.telephonyAllowOutgoing || "enabled");
    setShowOnTicket(settings.telephonyShowOnTicket || "enabled");
    setApiKey(settings.telephonyApiKey || "");
    setApiSecret(settings.telephonyApiSecret || "");
    setSipServer(settings.telephonySipServer || "pbx.zadarma.com");
    setWebsocketUrl(settings.telephonyWebsocketUrl || "wss://pbx.zadarma.com/ws");
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await update({ column: "telephonyEnabled", data: enabled });
      await update({ column: "telephonyProvider", data: provider });
      await update({ column: "telephonyAllowIncoming", data: allowIncoming });
      await update({ column: "telephonyAllowOutgoing", data: allowOutgoing });
      await update({ column: "telephonyShowOnTicket", data: showOnTicket });
      await update({ column: "telephonyApiKey", data: apiKey });
      await update({ column: "telephonyApiSecret", data: apiSecret });
      await update({ column: "telephonySipServer", data: sipServer });
      await update({ column: "telephonyWebsocketUrl", data: websocketUrl });
      toast.success("Configuracion de telefonia guardada.");
    } catch (_error) {
      toast.error("No se pudo guardar la configuracion de telefonia.");
    }
    setSaving(false);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.sectionTitle}>
        Telefonia de la empresa
      </Typography>
      <Typography className={classes.subtitle}>
        Active o desactive la telefonia por empresa y defina las credenciales base del proveedor.
      </Typography>

      <Box className={classes.infoBox}>
        <Typography variant="body2" style={{ marginBottom: 8 }}>
          Esta configuracion controla si el boton de llamada aparece en los tickets y habilita la generacion de la clave temporal WebRTC de Zadarma.
        </Typography>
        <Typography variant="body2">Luego, cada usuario debe tener su extension activa en su ficha.</Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <FormControl variant="outlined" fullWidth className={classes.formField}>
            <InputLabel>Telefonia</InputLabel>
            <Select value={enabled} onChange={(e) => setEnabled(e.target.value)} label="Telefonia">
              <MenuItem value="enabled">Activa</MenuItem>
              <MenuItem value="disabled">Inactiva</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl variant="outlined" fullWidth className={classes.formField}>
            <InputLabel>Proveedor</InputLabel>
            <Select value={provider} onChange={(e) => setProvider(e.target.value)} label="Proveedor">
              <MenuItem value="zadarma">Zadarma</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl variant="outlined" fullWidth className={classes.formField}>
            <InputLabel>Salientes empresa</InputLabel>
            <Select value={allowOutgoing} onChange={(e) => setAllowOutgoing(e.target.value)} label="Salientes empresa">
              <MenuItem value="enabled">Permitidas</MenuItem>
              <MenuItem value="disabled">Bloqueadas</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl variant="outlined" fullWidth className={classes.formField}>
            <InputLabel>Entrantes empresa</InputLabel>
            <Select value={allowIncoming} onChange={(e) => setAllowIncoming(e.target.value)} label="Entrantes empresa">
              <MenuItem value="enabled">Permitidas</MenuItem>
              <MenuItem value="disabled">Bloqueadas</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl variant="outlined" fullWidth className={classes.formField}>
            <InputLabel>Boton en tickets</InputLabel>
            <Select value={showOnTicket} onChange={(e) => setShowOnTicket(e.target.value)} label="Boton en tickets">
              <MenuItem value="enabled">Mostrar</MenuItem>
              <MenuItem value="disabled">Ocultar</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Servidor SIP"
            variant="outlined"
            fullWidth
            className={classes.formField}
            value={sipServer}
            onChange={(e) => setSipServer(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="WebSocket WebRTC"
            variant="outlined"
            fullWidth
            className={classes.formField}
            value={websocketUrl}
            onChange={(e) => setWebsocketUrl(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="API Key Zadarma"
            variant="outlined"
            fullWidth
            className={classes.formField}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="API Secret Zadarma"
            type={showSecret ? "text" : "password"}
            variant="outlined"
            fullWidth
            className={classes.formField}
            value={apiSecret}
            onChange={(e) => setApiSecret(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowSecret((prev) => !prev)} edge="end">
                    {showSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>
      </Grid>

      <div className={classes.buttonGroup}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Guardando..." : "Guardar telefonia"}
        </Button>
      </div>
    </div>
  );
};

export default TelephonyConfig;
